// Runs after index.js
import {
    getLocaleOverride,
    getParentLocale,
    gettext,
    setupAsync,
} from '@eventbrite/i18n';
import { loadableReady } from '@loadable/component';
import * as Sentry from '@sentry/react';
import { createHistory } from 'history';
import React from 'react';
import { hydrate } from 'react-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { dehydrate, Hydrate } from 'react-query/hydration';
import App from './app';
import { DEFAULT_QUERYCLIENT_OPTIONS } from './constants/reactQuery';

// retrieve data from server to hydrate the client
const props: any = window.__SERVER_DATA__ || {};

// retrieve dehydrated react-query state to hydrate
const queryClient = new QueryClient(DEFAULT_QUERYCLIENT_OPTIONS);
const dehydratedState = window.__REACT_QUERY_STATE__;

const genericDynamicImportErrorHandler = (err: any) => {
    if (process.env.NODE_ENV !== 'production') {
        throw err;
    }
    return null;
};

const parentLocale = getParentLocale(
    getLocaleOverride() || props.env.localeInfo.locale,
);

const errorMessage = () => {
    return <>{gettext('An error has occured')}</>;
};

const main = async () => {
    try {
        if (parentLocale !== 'en_US') {
            await setupAsync({
                parentLocale,
                translations: /* webpackChunkName: "discover-i18n-translation" */ import(
                    `./i18n/translations/${parentLocale}.json`
                ),
            });
        }
    } finally {
        loadableReady(() => {
            //We are not performing any pre-fetch calls
            //on the server render. So this should
            //provide a safe fallback that keeps
            //"default" behavior if an asset is missing
            //from the window.

            //Additionally in the homepage case,
            //due to how we include assets via ESI tags,
            //it will share the dehydratedState across
            //requests regardless. This ensures we default
            //to a clean query cache on each load
            const safeDehydratedState = dehydratedState
                ? dehydratedState
                : dehydrate(queryClient);

            hydrate(
                <Sentry.ErrorBoundary fallback={errorMessage}>
                    <QueryClientProvider client={queryClient}>
                        <Hydrate state={safeDehydratedState}>
                            <App
                                {...props}
                                history={createHistory()}
                                originalContext={props}
                            />
                        </Hydrate>
                    </QueryClientProvider>
                </Sentry.ErrorBoundary>,
                document.getElementById('root'),
            );
        });
    }
};

main();

// Split out to keep initial bundle small
import('@eventbrite/site-analytics')
    .then(({ initDimensionsFromState, trackInitialPageView }) => {
        // set up initial GA tracking
        initDimensionsFromState(props);

        // Don't make this call on Search because Search handles the initial page view tracking already
        if (
            window.location &&
            window.location.pathname &&
            !(window.location.pathname.indexOf('/d/') === 0)
        ) {
            trackInitialPageView(props.gaSettings);
        }
    })
    .catch(genericDynamicImportErrorHandler);
